import { useState, useEffect } from 'react'
import { Link, useParams } from 'react-router-dom'
import { Logo } from '../ui/Logo'
import { useI18n } from '../../i18n'
import { useWebsite } from '../../hooks/use-website'
import { ArrowLeft, BarChart3, MessageSquare, Eye, Zap, Lightbulb } from 'lucide-react'

export default function WebsiteAnalyticsPage() {
  const { websiteId } = useParams<{ websiteId: string }>()
  const { t } = useI18n()
  const { website, fetchWebsite, analytics, insights, fetchAnalytics, fetchInsights } = useWebsite()

  useEffect(() => {
    fetchWebsite(websiteId)
    if (websiteId) {
      fetchAnalytics(websiteId, 30)
      fetchInsights(websiteId)
    }
  }, [websiteId, fetchWebsite, fetchAnalytics, fetchInsights])

  if (!analytics) {
    return (
      <div className="min-h-screen bg-[#fafafa]">
        <header className="h-14 bg-white/70 backdrop-blur-2xl border-b border-black/[0.04] flex items-center px-4 sticky top-0 z-30">
          <Link to="/" className="flex items-center gap-2 text-lg font-semibold mr-6 shrink-0">
            <Logo size={24} />
            <span className="text-[#1a1a1a]">PrimeBook</span>
            <span className="text-orange-500">AI</span>
          </Link>
        </header>
        <div className="flex items-center justify-center py-32 text-gray-300 text-sm">{t('暂无数据', 'No data yet')}</div>
      </div>
    )
  }

  const stats = [
    { icon: Eye, label: t('页面浏览', 'Page Views'), value: analytics.totalPageViews, color: 'text-blue-500', bg: 'bg-blue-50' },
    { icon: MessageSquare, label: t('聊天会话', 'Chat Sessions'), value: analytics.totalChatSessions, color: 'text-orange-500', bg: 'bg-orange-50' },
    { icon: Zap, label: t('聊天消息', 'Chat Messages'), value: analytics.totalChatMessages, color: 'text-violet-500', bg: 'bg-violet-50' },
    { icon: BarChart3, label: t('转化率', 'Conversion'), value: `${analytics.chatConversionRate}%`, color: 'text-emerald-500', bg: 'bg-emerald-50' },
  ]

  return (
    <div className="min-h-screen bg-[#fafafa]">
      <header className="h-14 bg-white/70 backdrop-blur-2xl border-b border-black/[0.04] flex items-center px-4 sticky top-0 z-30">
        <Link to="/" className="flex items-center gap-2 text-lg font-semibold mr-6 shrink-0">
          <Logo size={24} />
          <span className="text-[#1a1a1a]">PrimeBook</span>
          <span className="text-orange-500">AI</span>
        </Link>
        <div className="ml-auto">
          <Link to={`/website/${websiteId}/edit`} className="px-3 py-1.5 text-[13px] rounded-lg text-gray-400 hover:text-[#1a1a1a] hover:bg-black/[0.04] transition-all">
            <ArrowLeft className="h-3.5 w-3.5 inline mr-1.5" />
            {t('返回编辑器', 'Back')}
          </Link>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-8">
        <h1 className="text-xl font-semibold text-[#1a1a1a] mb-1">{t('访客分析', 'Visitor Analytics')}</h1>
        <p className="text-sm text-gray-400 mb-6">{t('官网访客行为和AI管家互动数据', 'Website visitor behavior and AI Butler interaction data')}</p>

        {/* Overview stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          {stats.map((s, i) => (
            <div key={i} className="p-5 rounded-2xl bg-white border border-black/[0.04]">
              <div className={`h-9 w-9 rounded-xl ${s.bg} flex items-center justify-center mb-3`}>
                <s.icon className={`h-4 w-4 ${s.color}`} />
              </div>
              <div className="text-2xl font-bold text-[#1a1a1a]">{typeof s.value === 'number' ? s.value.toLocaleString() : s.value}</div>
              <div className="text-[11px] text-gray-400 mt-0.5">{s.label}</div>
            </div>
          ))}
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          {/* Top pages */}
          <div className="p-5 rounded-2xl bg-white border border-black/[0.04]">
            <h3 className="text-sm font-semibold text-[#1a1a1a] mb-4">{t('热门页面', 'Top Pages')}</h3>
            <div className="space-y-2">
              {(analytics.topPages || []).map((p, i) => (
                <div key={i} className="flex items-center justify-between py-2 px-3 rounded-xl bg-black/[0.02]">
                  <span className="text-xs text-gray-600">/{p.slug}</span>
                  <span className="text-xs font-medium text-[#1a1a1a]">{p.views.toLocaleString()}</span>
                </div>
              ))}
              {analytics.topPages?.length === 0 && <p className="text-xs text-gray-300">{t('暂无数据', 'No data')}</p>}
            </div>
          </div>

          {/* Top questions */}
          <div className="p-5 rounded-2xl bg-white border border-black/[0.04]">
            <h3 className="text-sm font-semibold text-[#1a1a1a] mb-4">{t('热门问题', 'Top Questions')}</h3>
            <div className="space-y-2">
              {(analytics.topQuestions || []).map((q, i) => (
                <div key={i} className="flex items-center justify-between py-2 px-3 rounded-xl bg-black/[0.02]">
                  <span className="text-xs text-gray-600 truncate mr-3">{q.question}</span>
                  <span className="text-xs font-medium text-orange-500 shrink-0">{q.count}x</span>
                </div>
              ))}
              {analytics.topQuestions?.length === 0 && <p className="text-xs text-gray-300">{t('暂无数据', 'No data')}</p>}
            </div>
          </div>
        </div>

        {/* AI Insights */}
        {insights.length > 0 && (
          <div className="mt-8">
            <h3 className="text-sm font-semibold text-[#1a1a1a] mb-4 flex items-center gap-2">
              <Lightbulb className="h-4 w-4 text-amber-500" />
              {t('AI 洞察建议', 'AI Insights')}
            </h3>
            <div className="space-y-3">
              {insights.map((insight, i) => (
                <div key={i} className="p-4 rounded-2xl bg-gradient-to-r from-amber-50/60 to-orange-50/40 border border-amber-100/50">
                  <p className="text-xs text-gray-600 mb-2">{insight.message}</p>
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100/60 text-amber-600 font-medium">
                      {Math.round(insight.confidence * 100)}% {t('置信度', 'confidence')}
                    </span>
                    {insight.action && (
                      <button className="text-[10px] px-2 py-0.5 rounded bg-orange-500 text-white font-medium hover:bg-orange-600 transition-colors">
                        {t('执行', 'Apply')}
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </main>
    </div>
  )
}
